import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, ArrowRight, Loader2, Clock, MessageCircle, ShieldCheck, BadgeCheck, Paperclip, X, ImageIcon } from "lucide-react";
import { contactInfo, services, openingHours } from "../mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactCards = [
  { icon: Phone, label: "Telefon", value: contactInfo.phone, href: contactInfo.phoneHref },
  { icon: Mail, label: "E-Mail", value: contactInfo.email, href: `mailto:${contactInfo.email}` },
  { icon: MapPin, label: "Standort", value: contactInfo.address, href: contactInfo.mapHref },
];

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState([]);

  const MAX_FILES = 5;
  const MAX_SIZE = 4 * 1024 * 1024; // 4 MB pro Bild

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = [];
    for (const file of selected) {
      if (!file.type.startsWith("image/")) {
        toast({ title: "Nur Bilder erlaubt", description: `"${file.name}" ist kein Bild.` });
        continue;
      }
      if (file.size > MAX_SIZE) {
        toast({ title: "Bild zu groß", description: `"${file.name}" überschreitet 4 MB.` });
        continue;
      }
      valid.push(file);
    }
    setFiles((prev) => [...prev, ...valid].slice(0, MAX_FILES));
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Bitte alle Pflichtfelder ausfüllen",
        description: "Name, E-Mail und Nachricht sind erforderlich.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const attachments = await Promise.all(
        files.map(async (f) => ({ filename: f.name, content: await fileToBase64(f) }))
      );
      await axios.post(`${API}/contact`, { ...form, attachments });
      toast({
        title: "Anfrage gesendet!",
        description: "Vielen Dank. Wir melden uns innerhalb von 24 Stunden bei Ihnen.",
      });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
      setFiles([]);
    } catch (err) {
      toast({
        title: "Es ist ein Fehler aufgetreten",
        description: "Bitte versuchen Sie es später erneut oder rufen Sie uns an.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition";

  return (
    <>
      <section id="kontakt" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            {/* Left */}
            <div>
              <div className="reveal">
                <span className="text-[13px] font-bold tracking-[0.22em] text-blue-600 uppercase">
                  Kontakt
                </span>
                <h2 className="font-display mt-3 text-slate-900 text-[28px] sm:text-[44px] tracking-tight leading-[1.05]" style={{ fontWeight: 900 }}>
                  Kostenloses Angebot anfordern
                </h2>
                <p className="mt-4 text-[17px] text-slate-600 leading-relaxed max-w-lg">
                  Erzählen Sie uns von Ihrem Objekt. Wir melden uns innerhalb von
                  24 Stunden mit einem individuellen und transparenten Angebot.
                </p>
              </div>

              <div className="mt-8 space-y-4">
                {contactCards.map((c) => (
                  <a
                    key={c.label}
                    href={c.href}
                    target={c.label === "Standort" ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="reveal group flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 hover:border-blue-300 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors shrink-0">
                      <c.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[13px] font-semibold text-slate-500">{c.label}</div>
                      <div className="text-[15px] font-semibold text-slate-900">{c.value}</div>
                    </div>
                  </a>
                ))}
              </div>

              {/* WhatsApp Schnellkontakt */}
              <a
                href={contactInfo.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="reveal group mt-4 flex items-center gap-4 rounded-xl bg-[#25D366] p-4 text-white shadow-lg shadow-green-600/25 hover:bg-[#1fb857] transition-colors"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 shrink-0">
                  <MessageCircle className="w-5 h-5" fill="currentColor" strokeWidth={0} />
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-white/80">Schnell & unkompliziert</div>
                  <div className="text-[15px] font-bold">Jetzt per WhatsApp schreiben</div>
                </div>
                <ArrowRight className="ml-auto w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>

              {/* Öffnungszeiten */}
              <div className="reveal mt-5 rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span className="text-[14px] font-bold text-slate-900">Öffnungszeiten</span>
                </div>
                <ul className="space-y-1.5">
                  {openingHours.map((o) => (
                    <li key={o.day} className="flex items-center justify-between text-[14px]">
                      <span className="text-slate-600">{o.day}</span>
                      <span className="font-semibold text-slate-900">{o.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Form */}
            <div className="reveal rounded-3xl border border-slate-200 bg-slate-50 p-7 lg:p-9 shadow-sm">
              {/* Vertrauens-Badges */}
              <div className="flex flex-wrap gap-x-5 gap-y-2 mb-6">
                {[
                  { icon: Clock, label: "Antwort in 24 Std." },
                  { icon: BadgeCheck, label: "Kostenlos & unverbindlich" },
                  { icon: ShieldCheck, label: "100 % versichert" },
                ].map((b) => (
                  <div key={b.label} className="flex items-center gap-2 text-[13px] font-semibold text-slate-700">
                    <b.icon className="w-4 h-4 text-blue-600" strokeWidth={2.2} />
                    {b.label}
                  </div>
                ))}
              </div>
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-[14px] font-semibold text-slate-700 mb-1.5">
                      Name <span className="text-blue-600">*</span>
                    </label>
                    <input id="contact-name" name="name" autoComplete="name" aria-required="true" className={inputClass} value={form.name} onChange={update("name")} placeholder="Ihr Name" />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-[14px] font-semibold text-slate-700 mb-1.5">
                      E-Mail <span className="text-blue-600">*</span>
                    </label>
                    <input id="contact-email" name="email" type="email" autoComplete="email" aria-required="true" className={inputClass} value={form.email} onChange={update("email")} placeholder="name@firma.de" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-phone" className="block text-[14px] font-semibold text-slate-700 mb-1.5">
                      Telefon
                    </label>
                    <input id="contact-phone" name="phone" type="tel" autoComplete="tel" className={inputClass} value={form.phone} onChange={update("phone")} placeholder="+49 ..." />
                  </div>
                  <div>
                    <label htmlFor="contact-service" className="block text-[14px] font-semibold text-slate-700 mb-1.5">
                      Gewünschte Leistung
                    </label>
                    <select id="contact-service" name="service" className={inputClass + " appearance-none cursor-pointer"} value={form.service} onChange={update("service")}>
                      <option value="">Bitte auswählen</option>
                      {services.map((s) => (
                        <option key={s.id} value={s.title}>{s.title}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-[14px] font-semibold text-slate-700 mb-1.5">
                    Nachricht <span className="text-blue-600">*</span>
                  </label>
                  <textarea id="contact-message" name="message" rows={4} aria-required="true" className={inputClass + " resize-none"} value={form.message} onChange={update("message")} placeholder="Beschreiben Sie kurz Ihr Objekt (Größe, Häufigkeit, Wunschtermin)..." />
                </div>

                {/* Bild-Upload */}
                <div>
                  <label className="block text-[14px] font-semibold text-slate-700 mb-1.5">
                    Bilder anhängen <span className="font-normal text-slate-400">(optional, z. B. Fotos Ihres Objekts)</span>
                  </label>
                  <label
                    htmlFor="contact-files"
                    className="flex items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-3.5 cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
                    data-testid="contact-file-label"
                  >
                    <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 text-blue-600 shrink-0">
                      <Paperclip className="w-5 h-5" />
                    </span>
                    <span className="text-[14px] text-slate-600">
                      Bilder auswählen <span className="text-slate-400">(max. 5 Bilder, je bis 4 MB)</span>
                    </span>
                    <input
                      id="contact-files"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleFiles}
                      data-testid="contact-file-input"
                    />
                  </label>

                  {files.length > 0 && (
                    <ul className="mt-3 space-y-2" data-testid="contact-file-list">
                      {files.map((f, i) => (
                        <li key={i} className="flex items-center gap-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                          <ImageIcon className="w-4 h-4 text-blue-600 shrink-0" />
                          <span className="text-[13px] text-slate-700 truncate flex-1">{f.name}</span>
                          <span className="text-[12px] text-slate-400">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                          <button
                            type="button"
                            onClick={() => removeFile(i)}
                            aria-label="Bild entfernen"
                            data-testid={`remove-file-${i}`}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <p className="text-[13px] text-slate-500">
                  Mit dem Absenden akzeptieren Sie unsere Datenschutzerklärung.
                </p>

                <button
                  type="submit"
                  disabled={submitting}
                  className="group w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-blue-600 text-white text-[15px] font-semibold shadow-xl shadow-blue-600/25 hover:bg-blue-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      Anfrage senden
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
